/**
 * Stats System - Main Export
 *
 * 5-Layer Architecture for Discipline Statistics
 *
 * Layer 1: Data Collection (collectors/)
 * - Capture events from MentionService & ConsequenceService
 * - Extract statistical dimensions
 * - Route to storage layer
 * * Layer 2: Storage & Persistence (storage/)
 * - Persist events to Firestore
 * - Atomic counter updates
 * - Provide read access
 *
 * Layer 3: Aggregation & Query (aggregation/)
 * - Calculate baselines and trends
 * - Detect anomalies
 * - Compute complex metrics
 *
 * Layer 4: Intelligence & Insights (intelligence/) [TO BE BUILT]
 * - Analyze student risk
 * - Identify patterns
 * - Generate predictions
 *
 * Layer 5: Presentation & API (presentation/) [TO BE BUILT]
 * - Format for Dashboard
 * - Format for Teacher View
 * - Public API interface
 *
 * Usage:
 * import { statsOrchestrator, storageService, baselineCalculator } from '../services/discipline/stats';
 *
 * // In MentionService
 * await statsOrchestrator.onMentionCreated(mention);
 *
 * // In Dashboard
 * const todayStats = await storageService.getTodayStats();
 * const baseline = await baselineCalculator.getBaseline('28-day');
 */

// Layer 1: Collectors
export { BaseCollector } from './collectors/BaseCollector.js';
export { MentionCollector } from './collectors/MentionCollector.js';
export { ConsequenceCollector } from './collectors/ConsequenceCollector.js';
export { CollectorOrchestrator } from './collectors/CollectorOrchestrator.js';

// Layer 2: Storage (v2 - calculates on-demand with optional caching)
export { StorageService, storageService } from './storage/StorageService.v2.js';

// Layer 3: Aggregation
export { BaselineCalculator } from './aggregation/BaselineCalculator.js';
export { TrendCalculator } from './aggregation/TrendCalculator.js';
export { AnomalyDetector } from './aggregation/AnomalyDetector.js';

// Layer 4: Intelligence (to be built)
// export { StudentRiskAnalyzer } from './intelligence/StudentRiskAnalyzer.js';
// export { ClassroomAnalyzer } from './intelligence/ClassroomAnalyzer.js';
// export { PatternRecognizer } from './intelligence/PatternRecognizer.js';
// export { PredictionEngine } from './intelligence/PredictionEngine.js';

// Layer 5: Presentation (to be built)
// export { DashboardFormatter } from './presentation/DashboardFormatter.js';
// export { TeacherViewFormatter } from './presentation/TeacherViewFormatter.js';
// export { StatsAPIService, statsAPI } from './presentation/StatsAPIService.js';

// Initialize services (singletons)
import { CollectorOrchestrator } from './collectors/CollectorOrchestrator.js';
import { storageService } from './storage/StorageService.v2.js';
import { BaselineCalculator } from './aggregation/BaselineCalculator.js';
import { TrendCalculator } from './aggregation/TrendCalculator.js';
import { AnomalyDetector } from './aggregation/AnomalyDetector.js';

export const statsOrchestrator = new CollectorOrchestrator(storageService);
export const baselineCalculator = new BaselineCalculator(storageService);
export const trendCalculator = new TrendCalculator();
export const anomalyDetector = new AnomalyDetector();

/**
 * Health check for stats system
 * @returns {Object}
 */
export function getStatsSystemHealth() {
  return {
    layer1: {
      orchestrator: statsOrchestrator.isEnabled(),
      collectors: statsOrchestrator.getHealth()
    },
    layer2: {
      storage: !!storageService
    },
    layer3: {
      aggregation: !!(baselineCalculator && trendCalculator && anomalyDetector)
    },
    layer4: {
      intelligence: false // Not yet built
    },
    layer5: {
      presentation: false // Not yet built
    }
  };
}
